import { Notifier } from "@airbrake/browser";
import { composeWithDevTools } from "@redux-devtools/extension";
import { applyMiddleware, createStore } from "redux";
import * as reduxAirbrake from "redux-airbrake";
import { createEpicMiddleware } from "redux-observable";
import thunkMiddleware from "redux-thunk";

import { DEV_DOMAIN, LOCAL_DOMAIN, UAT_DOMAIN } from "@/constants/routes";
import { getKubernetesCluster, getKubernetesNamespaceAndApp } from "@/util/domain";
import rootEpic from "./root-epic";
import createRootReducer from "./root-reducer";

// Workaround: redux-airbrake v3 has no ESM build, so webpack 5 wraps its
// CJS export in `.default` (sometimes twice). Unwrap to get the real function.
const airbrakeMiddleware = (() => {
  const mod = reduxAirbrake.default || reduxAirbrake;
  return typeof mod === "function" ? mod : mod.default;
})();

if (typeof airbrakeMiddleware !== "function") {
  throw new Error("redux-airbrake: failed to resolve middleware factory");
}

const hostname = window.location.hostname;

const IS_NON_PROD_ENV =
  [DEV_DOMAIN, LOCAL_DOMAIN, UAT_DOMAIN].some((url) => hostname.includes(url)) ||
  process.env.NODE_ENV !== "production";

const IS_NON_UAT_PROD_ENV =
  [DEV_DOMAIN, LOCAL_DOMAIN].some((url) => hostname.includes(url)) ||
  (process.env.NODE_ENV !== "production" && process.env.NODE_ENV !== "uat");

const KUBERNETES_ENVIRONMENT = getKubernetesCluster(hostname);

const KUBERNETES_NAMESPACE = getKubernetesNamespaceAndApp(hostname);

// This integrates the app with the Redux DevTools browser extension.
// For all available options, see:
//   https://github.com/zalmoxisus/redux-devtools-extension/blob/master/docs/API/Arguments.md
const composeEnhancers = composeWithDevTools({ trace: IS_NON_PROD_ENV });

const epicMiddleware = createEpicMiddleware();

const createDashboardStore = (config) => {
  const airbrake = new Notifier({
    projectId: 242638,
    projectKey: "b341a7bb115259b332afd77ff4199603",
    environment: KUBERNETES_ENVIRONMENT,
    namespace: KUBERNETES_NAMESPACE,
  });

  airbrake.addFilter((notice) => {
    if (IS_NON_UAT_PROD_ENV) return null;

    notice.context.environment = KUBERNETES_ENVIRONMENT;

    return notice;
  });

  const errorMiddleware = airbrakeMiddleware(airbrake, {
    noticeAnnotations: { context: { environment: KUBERNETES_ENVIRONMENT } },
  });

  const store = createStore(
    createRootReducer,
    { config },
    composeEnhancers(
      applyMiddleware(thunkMiddleware, epicMiddleware, errorMiddleware)
    )
  );

  epicMiddleware.run(rootEpic());

  return store;
};

export default createDashboardStore;
